import { PrivacyTip, StickyNote2 } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  Switch,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChangeEvent, FC, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { usePopover } from '../../hooks/use-popover';
import { ChevronDown as ChevronDownIcon } from '../../icons/chevron-down';
import { Logout as LogoutIcon } from '../../icons/logout';
import { InputField } from '../atoms/input-field';
import { SearchableSelect } from '../atoms/searchable-select';

import { useAuth } from '@/features/auth/contexts/auth-provider';
import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { useAvailableOrganizations } from '@/features/shared/hooks/query/use-available-organizations';
import { useUserStore } from '@/store/user-store';

interface AccountPopoverProps {
  darkMode: boolean;
  onLanguageChange: (newLanguage: 'en' | 'pl') => void;
  onSwitchTheme: () => void;
}

const languageOptions = {
  en: {
    label: 'English',
  },
  pl: {
    label: 'Polish',
  },
};

const AccountPopover: FC<AccountPopoverProps> = ({
  darkMode,
  onLanguageChange,
  onSwitchTheme,
  ...other
}) => {
  const { i18n, t } = useTranslation(['common']);

  const { session, logout } = useAuth();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [anchorRef, open, handleOpen, handleClose] = usePopover();

  const setOrganizationToken = useUserStore(state => state.setOrganizationToken);
  const setSiteToken = useUserStore(state => state.setSiteToken);

  const organizationToken = useUserStore(state => state.organizationToken);

  const siteToken = useUserStore(state => state.siteToken);

  const { data: availableOrganizationsData } = useAvailableOrganizations();

  const currentOrganization = availableOrganizationsData?.find(
    organization => organization.token === organizationToken,
  );

  const currentSite = currentOrganization?.sites?.find(site => site.token === siteToken);

  const organizationOptions = useMemo(
    () =>
      availableOrganizationsData
        ?.map(organization => ({
          value: organization.token,
          label: organization.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [availableOrganizationsData],
  );

  const siteOptions = useMemo(
    () =>
      currentOrganization?.sites
        ?.map(site => ({
          value: site.token,
          label: site.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [currentOrganization],
  );

  const handleOrganizationChange = async (newOrganizationToken: string) => {
    try {
      const newOrganization = availableOrganizationsData?.find(
        organization => organization.token === newOrganizationToken,
      );

      setOrganizationToken(newOrganizationToken);
      setSiteToken(newOrganization?.sites?.[0]?.token ?? '');

      handleClose();

      queryClient.invalidateQueries();
      navigate({ to: '/visits' });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSiteChange = async (newSiteToken: string) => {
    try {
      setSiteToken(newSiteToken);

      handleClose();

      queryClient.invalidateQueries();
      navigate({ to: '/visits' });
    } catch (error) {
      console.log(error);
    }
  };

  const handleLanguageChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onLanguageChange(event.target.value as 'en' | 'pl');
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      handleClose();

      queryClient.invalidateQueries();
      queryClient.removeQueries();
    } catch (err) {
      toast.error(t('common:somethingWentWrong'));
    }
  };

  const handleOpenPrivacyPolicy = () => {
    window.open(
      `https://storage.googleapis.com/acs-assets/privacy-policy-${i18n.language}.pdf`,
      '_blank',
    );
  };

  const handleOpenTermsAndConditions = () => {
    window.open(
      `https://storage.googleapis.com/acs-assets/terms-of-use-${i18n.language}.pdf`,
      '_blank',
    );
  };

  const renderAvatar = () => {
    return (
      <Avatar
        src={session?.user?.email?.charAt(0).toUpperCase() ?? ''}
        variant="rounded"
        sx={{
          height: 40,
          width: 40,
        }}
      />
    );
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          ml: 2,
        }}
        {...other}>
        {renderAvatar()}
        <Box
          sx={{
            alignItems: 'center',
            display: {
              md: 'flex',
              xs: 'none',
            },
            flex: 1,
            ml: 1,
            minWidth: 120,
          }}>
          <div>
            <Typography sx={{ color: 'primary.contrastText' }} variant="subtitle2">
              {session?.user?.email}
            </Typography>
          </div>
          <ChevronDownIcon
            sx={{
              color: 'primary.contrastText',
              ml: 1,
            }}
          />
        </Box>
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 'calc(100vw - 32px)',
            width: 300,
          },
        }}>
        <List sx={{ p: 0 }}>
          <ListItem>
            <ListItemAvatar>{renderAvatar()}</ListItemAvatar>
            <ListItemText
              primary={session?.user?.email}
              secondary={currentOrganization?.name + ' - ' + currentSite?.name}
              sx={{
                minWidth: 0,
                '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                  overflowWrap: 'anywhere',
                  whiteSpace: 'normal',
                },
              }}
            />
          </ListItem>

          {organizationOptions.length > 1 && (
            <ListItem sx={{ pt: 0, pb: 1 }}>
              <SearchableSelect
                fullWidth
                onChange={value => {
                  handleOrganizationChange(value);
                }}
                options={organizationOptions}
                value={organizationToken ?? ''}
                hideSearch={organizationOptions.length < 5}
              />
            </ListItem>
          )}

          {siteOptions.length > 1 && (
            <ListItem sx={{ pt: 0, pb: 2 }}>
              <SearchableSelect
                fullWidth
                hideSearch={siteOptions.length < 5}
                onChange={value => {
                  handleSiteChange(value);
                }}
                options={siteOptions}
                value={siteToken ?? ''}
              />
            </ListItem>
          )}

          <li>
            <List disablePadding>
              <ListSubheader
                sx={{
                  display: {
                    md: 'none',
                    xs: 'flex',
                  },
                }}
                disableSticky>
                {t('common:appSettings')}
              </ListSubheader>
              <ListItem
                sx={{
                  display: {
                    md: 'none',
                    xs: 'flex',
                  },
                }}>
                <InputField
                  fullWidth
                  onChange={handleLanguageChange}
                  select
                  SelectProps={{ native: true }}
                  value={i18n.language}>
                  {Object.keys(languageOptions).map(option => (
                    <option key={option} value={option}>
                      {languageOptions[option as OrganizationLanguage]?.label}
                    </option>
                  ))}
                </InputField>
              </ListItem>
              <ListItem
                sx={{
                  py: 0,
                  display: {
                    md: 'none',
                    xs: 'flex',
                  },
                }}>
                <Switch checked={darkMode} onChange={onSwitchTheme} />
                <Typography color="textPrimary" variant="body2">
                  {t('common:darkMode')}
                </Typography>
              </ListItem>
            </List>
          </li>
          <Divider />
          <ListItemButton onClick={handleOpenTermsAndConditions}>
            <ListItemIcon>
              <PrivacyTip />
            </ListItemIcon>
            <ListItemText primary={t('common:termsAndConditions')} />
          </ListItemButton>
          <ListItemButton onClick={handleOpenPrivacyPolicy}>
            <ListItemIcon>
              <StickyNote2 />
            </ListItemIcon>
            <ListItemText primary={t('common:privacyPolicy')} />
          </ListItemButton>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('common:logOut')} />
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};

export default AccountPopover;
